/**
 * Inserting an extract of a template (its cover or closing pages) into the
 * document, and marking what was inserted so a later template can swap it.
 *
 * Word refused the 28 Sep cover inserts with a bare GeneralException on a
 * valid file. Two of the suspects are handled here: the insert goes into a
 * fresh paragraph placed outside any content control (the sync feature wraps
 * each answer in one, and the body used to be inserted straight at "Start"),
 * and when Word still refuses, the same pages are tried again without the
 * template's section properties (page setup is its own step anyway), and
 * last as a flat OOXML package through insertOoxml. The 28-30 Sep refusals
 * turned out to be the server's extract naming undeclared prefixes in
 * mc:Ignorable (fixed there); the ladder stays as belt and braces, and it
 * never inserts less than the whole pages. Change tracking is the caller's
 * business (see tracking.ts).
 */
import { describeError } from "./errors";

/** Content controls wrapping a template's cover/closing pages, so a later template can swap them. */
export const TEMPLATE_FRAME_TAG = "mytender-template-frame";

export type ExtractInsert = {
  docx_base64: string;
  /** The same pages, whole, as a flat OOXML package, for the last try through insertOoxml. */
  ooxml?: string;
  where: "start" | "end" | { after: number };
  /** Mark the inserted pages as this template's frame. */
  label?: string | null;
};

export type ExtractOutcome = {
  status: "inserted" | "error";
  /** Which form Word took: "full" (the file) or "ooxml" (the same pages as a package). */
  used: string;
  /** Word refused the file; the OOXML form went in (or nothing did). Nothing is lost either way. */
  degraded: boolean;
  /** What Word said to each form it refused, in order. */
  refusals: string[];
  tagged: boolean;
  message?: string;
};

/** Wrap a range in the hidden control that marks a template's frame pages. */
export const tagFrame = (range: Word.Range, label: string): void => {
  const control = range.insertContentControl();
  control.tag = TEMPLATE_FRAME_TAG;
  control.title = label;
  control.appearance = "Hidden";
  control.cannotDelete = false;
  control.cannotEdit = false;
};

/**
 * A fresh, empty paragraph at the start or end of the body, outside any
 * content control, for the extract to replace.
 */
const anchorParagraph = async (context: Word.RequestContext, where: "start" | "end"): Promise<Word.Paragraph> => {
  const body = context.document.body;
  const anchor = body.insertParagraph("", where === "start" ? Word.InsertLocation.start : Word.InsertLocation.end);
  const control = anchor.parentContentControlOrNullObject;
  control.load("isNullObject");
  await context.sync();
  if (control.isNullObject) return anchor;
  // Word put the new paragraph inside the first (or last) control. Step
  // outside it: insert relative to the control instead.
  anchor.delete();
  const outside = control.insertParagraph(
    "",
    where === "start" ? Word.InsertLocation.before : Word.InsertLocation.after
  );
  await context.sync();
  return outside;
};

const attempt = async (
  insert: ExtractInsert,
  content: { docx: string } | { ooxml: string }
): Promise<{ tagged: boolean }> =>
  Word.run(async (context) => {
    const putAfter = (target: Word.Range): Word.Range =>
      "docx" in content
        ? target.insertFileFromBase64(content.docx, Word.InsertLocation.after)
        : target.insertOoxml(content.ooxml, Word.InsertLocation.after);
    const replace = (target: Word.Paragraph): Word.Range =>
      "docx" in content
        ? target.insertFileFromBase64(content.docx, Word.InsertLocation.replace)
        : target.insertOoxml(content.ooxml, Word.InsertLocation.replace);
    let range: Word.Range;
    if (typeof insert.where === "object") {
      const paragraphs = context.document.body.paragraphs;
      paragraphs.load("items");
      await context.sync();
      const target = paragraphs.items[insert.where.after];
      if (!target) throw new Error(`No paragraph at index ${insert.where.after}.`);
      range = putAfter(target.getRange(Word.RangeLocation.whole));
    } else {
      const anchor = await anchorParagraph(context, insert.where);
      if (insert.where === "end") anchor.insertBreak(Word.BreakType.page, Word.InsertLocation.before);
      range = replace(anchor);
    }
    let tagged = false;
    if (insert.label) {
      tagFrame(range, insert.label);
      tagged = true;
    }
    await context.sync();
    return { tagged };
  });

/**
 * Insert the extract; on a refusal, once more without section properties.
 * Runs its own batches; call inside `withTrackingOff`.
 */
export const insertExtract = async (insert: ExtractInsert): Promise<ExtractOutcome> => {
  const refusals: string[] = [];
  const ladder: { used: string; content: { docx: string } | { ooxml: string } }[] = [
    { used: "full", content: { docx: insert.docx_base64 } },
    ...(insert.ooxml ? [{ used: "ooxml", content: { ooxml: insert.ooxml } }] : []),
  ];
  for (const step of ladder) {
    try {
      const { tagged } = await attempt(insert, step.content);
      const degraded = step.used !== "full";
      return {
        status: "inserted",
        used: step.used,
        degraded,
        refusals,
        tagged,
        ...(degraded && {
          message: `Word refused the pages as a file (${refusals.join("; ")}); inserted the same pages as OOXML.`,
        }),
      };
    } catch (error) {
      refusals.push(`${step.used}: ${describeError(error)}`);
    }
  }
  return { status: "error", used: "none", degraded: true, refusals, tagged: false, message: refusals.join("; ") };
};
